/* ============================================
   ProFRSH Cart Drawer - Slide-out Cart
   ============================================ */
window.Profrsh = window.Profrsh || {};

Profrsh.CartDrawer = class {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('[data-cart-drawer-panel]');
    this.overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
    this.isOpen = false;
    this._qtyDebounceTimers = {};   /* per-line debounce timers */
    this._pendingQtyUpdates = {};   /* batched quantity values   */

    this.bindEvents();
  }

  bindEvents() {
    /* Open triggers — support both data-open-cart-drawer and data-cart-toggle */
    document.querySelectorAll('[data-open-cart-drawer], [data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });

    /* Close triggers — overlay is outside the panel so direct binding is safe */
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    /* Note: [data-cart-drawer-close] buttons are handled via event delegation
       below (inside the drawer click handler) because refreshDrawer() replaces
       panel innerHTML and would destroy any directly-bound listeners. */

    /* ESC key */
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    /* Auto-open on item added */
    document.addEventListener('cart:item-added', () => this.open());

    /* Cart updated - refresh drawer content */
    document.addEventListener('cart:updated', () => this.refreshDrawer());

    /* Cart errors — show feedback to user */
    document.addEventListener('cart:error', (e) => {
      this.showError(e.detail?.message || 'Something went wrong. Please try again.');
      this.refreshDrawer();
    });

    /* Custom events API */
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:close', () => this.close());

    /* Delegated click handler — handles close, quantity, and remove buttons.
       Event delegation on this.drawer ensures buttons work even after
       refreshDrawer() replaces panel innerHTML. */
    this.drawer.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-cart-drawer-close]');
      if (closeBtn) { e.preventDefault(); this.close(); return; }

      const minusBtn = e.target.closest('[data-qty-minus]');
      const plusBtn = e.target.closest('[data-qty-plus]');
      const removeBtn = e.target.closest('[data-remove-item]');

      if (minusBtn || plusBtn) {
        e.preventDefault();
        const btn = minusBtn || plusBtn;
        const key = btn.dataset.lineKey;
        const currentQty = parseInt(btn.dataset.qty, 10) || 1;
        const newQty = plusBtn ? currentQty + 1 : Math.max(0, currentQty - 1);

        /* Optimistic UI — update the number & buttons instantly */
        this._optimisticQtyUpdate(btn, newQty);

        /* Remove immediately if quantity hits 0 */
        if (newQty === 0) {
          this._sendQtyUpdate(key, 0);
          return;
        }

        /* Debounce rapid taps — batch into one request after 400ms */
        this._pendingQtyUpdates[key] = newQty;
        clearTimeout(this._qtyDebounceTimers[key]);
        this._qtyDebounceTimers[key] = setTimeout(() => {
          const qty = this._pendingQtyUpdates[key];
          delete this._pendingQtyUpdates[key];
          this._sendQtyUpdate(key, qty);
        }, 400);
      }

      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.lineKey;
        /* Optimistic: fade out the line item immediately */
        const lineItem = removeBtn.closest('.cart-line-item');
        if (lineItem) {
          lineItem.style.opacity = '0.3';
          lineItem.style.pointerEvents = 'none';
        }
        this._sendQtyUpdate(key, 0);
      }
    });
  }

  open() {
    this.drawer.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      Profrsh.pauseBodyScroll();
      if (this.panel) Profrsh.trapFocus(this.panel);
    });
    this.refreshDrawer();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.isOpen = false;
    Profrsh.resumeBodyScroll();
    setTimeout(() => {
      if (!this.isOpen) this.drawer.setAttribute('hidden', '');
    }, 400);
  }

  showError(message) {
    let errorEl = this.drawer.querySelector('.cart-drawer__error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'cart-drawer__error';
      errorEl.setAttribute('role', 'alert');
      const header = this.drawer.querySelector('.cart-drawer__header');
      if (header) header.insertAdjacentElement('afterend', errorEl);
      else this.panel?.prepend(errorEl);
    }
    errorEl.textContent = message;
    errorEl.hidden = false;
    clearTimeout(this._errorTimeout);
    this._errorTimeout = setTimeout(() => { errorEl.hidden = true; }, 5000);
  }

  /* ---- Optimistic UI: instantly update the displayed quantity ---- */
  _optimisticQtyUpdate(btn, newQty) {
    const lineItem = btn.closest('.cart-line-item');
    if (!lineItem) return;

    /* Update the displayed number */
    const qtyValue = lineItem.querySelector('[data-qty-value]');
    if (qtyValue) qtyValue.textContent = newQty;

    /* Update data-qty on both +/- buttons so next click uses the new value */
    lineItem.querySelectorAll('[data-qty-minus], [data-qty-plus]').forEach(b => {
      b.dataset.qty = newQty;
    });

    /* Disable minus at 1 */
    const minusBtn = lineItem.querySelector('[data-qty-minus]');
    if (minusBtn) minusBtn.disabled = (newQty <= 1);

    /* Subtle pulse on the number */
    if (qtyValue) {
      qtyValue.style.transform = 'scale(1.2)';
      setTimeout(() => { qtyValue.style.transform = ''; }, 150);
    }
  }

  /* ---- Send the actual cart update (single network request) ---- */
  async _sendQtyUpdate(key, quantity) {
    this.drawer.classList.add('is-loading');
    try {
      if (window.profrshCart) {
        await window.profrshCart.updateItem(key, quantity);
      }
      /* cart:updated event fires from cart.js → refreshDrawer() picks it up */
    } catch (err) {
      /* On error, refresh to restore correct state */
      this.refreshDrawer();
    } finally {
      this.drawer.classList.remove('is-loading');
    }
  }

  async refreshDrawer() {
    try {
      const res = await fetch('/?sections=cart-drawer');
      if (!res.ok) return;
      const data = await res.json();
      const html = data['cart-drawer'];
      if (html) {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const newContent = temp.querySelector('[data-cart-drawer-panel]');
        if (newContent && this.panel) {
          this.panel.innerHTML = newContent.innerHTML;
        }
      }
    } catch (err) {
      /* Fallback: reload page data */
      console.warn('[CartDrawer] Section render failed, using fallback');
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  new Profrsh.CartDrawer();
});
